import { db, auth } from '../../firebase/config.js';
import { doc, setDoc, collection, query, where, deleteDoc } from 'firebase/firestore';
import { useCollection } from 'vuefire';
import { computed } from 'vue';
import { useAuditLog } from '../useAuditLog.js';
import { useExportHistory } from '../exports/useExportHistory.js';
import { useNotify } from '../useNotify.js'

export function useFormActions(formData) {
    const { logAction } = useAuditLog();
    const { logPdfExport } = useExportHistory();
    const notify = useNotify();

    const salvare = async () => {
        try {
            // check if user is authenticated
            if (!auth.currentUser) {
                notify.error('Trebuie să fii autentificat pentru a salva date!', 'Autentificare')
                return false
            }

            if (formData.examLocked?.value) {
                notify.info('Acest buletin este finalizat și nu poate fi modificat.', 'Blocat')
                return false
            }

            // save patient data first
            const patientRef = doc(db, 'patients', formData.cnpPacient.value)

            // Generate search tokens for easy searching
            const lastName = formData.lastNamePacient.value || ''
            const firstName = formData.firstNamePacient.value || ''
            const lastNameLower = lastName.toLowerCase()
            const firstNameLower = firstName.toLowerCase()
            const searchTokens = [
                lastNameLower,
                firstNameLower,
                `${lastNameLower} ${firstNameLower}`,
                `${firstNameLower} ${lastNameLower}`
            ].filter(Boolean)

            const patientData = {
                cnp: formData.cnpPacient.value,
                lastName: lastName,
                firstName: firstName,
                sex: formData.sexPacient.value,
                birthDate: formData.birthDatePacient.value,
                phone: formData.phonePacient.value || '',
                notes: formData.notesPacient.value || '',
                height: formData.inaltimePacient.value,
                weight: formData.greutatePacient.value,
                bsa: formData.bsaPacient.value,
                lastNameLower: lastNameLower,
                firstNameLower: firstNameLower,
                searchTokens: searchTokens,
                createdBy: auth.currentUser.uid,
                updatedAt: new Date().toISOString()
            }

            // Add createdAt only if it's a new patient
            let existingPatient = false;
            if (navigator.onLine) {
                try {
                    const { getDoc } = await import('firebase/firestore')
                    const snap = await getDoc(patientRef)
                    existingPatient = snap.exists()
                } catch (e) {
                    existingPatient = false;
                }
            }

            if (!existingPatient) {
                patientData.createdAt = new Date().toISOString()
            }

            await setDoc(patientRef, patientData, { merge: true })

            // Log patient action
            await logAction(
                existingPatient ? 'update_patient' : 'create_patient',
                'patient',
                formData.cnpPacient.value,
                {},
                { lastName, firstName }
            )

            // Get patient name for snapshot
            const patientNameSnapshot = `${lastName} ${firstName}`.trim()

            // Get doctor name for snapshot
            let doctorNameSnapshot = auth.currentUser.email;
            if (navigator.onLine) {
                try {
                    const { getDoc } = await import('firebase/firestore')
                    const doctorRef = doc(db, 'users', auth.currentUser.uid)
                    const doctorSnap = await getDoc(doctorRef)
                    doctorNameSnapshot = doctorSnap.exists() ? doctorSnap.data().name : auth.currentUser.email
                } catch (e) {
                    // Fallback to email if offline or error
                    doctorNameSnapshot = auth.currentUser.email;
                }
            }

            // save exam data with grouped measurements
            const examData = {
                patientId: formData.cnpPacient.value,
                patientNameSnapshot: patientNameSnapshot,
                doctorId: auth.currentUser.uid,
                doctorNameSnapshot: doctorNameSnapshot,
                dateConsult: formData.dataConsult.value,
                status: formData.examStatus?.value || "draft", // draft | final | void
                locked: !!formData.examLocked?.value,

                measurements: {
                    lv: {
                        dtdvs: formData.DTDVS.value,
                        dtdvsBsa: formData.DTDVSbsa.value,
                        dtsvs: formData.DTSVS.value,
                        dtsvsBsa: formData.DTSVSbsa.value,
                        sivd: formData.SIVd.value,
                        ppvsd: formData.PPVSd.value,
                        masaVs: formData.MasaVS.value,
                        rtw: formData.RTW.value,
                        vtdvs: formData.VTDVS.value,
                        vtdvsBsa: formData.VTDVSbsa.value,
                        vtsvs: formData.VTSVS.value,
                        vtsvsBsa: formData.VTSVSbsa.value,
                        feVol: formData.FEvol.value,
                        fs: formData.FS.value,
                        fe: formData.FE.value,
                        mapse: formData.MAPSE.value
                    },
                    mitral: {
                        dpMax: formData.DPmaxMitral.value,
                        dpMediu: formData.DPmediuMitral.value,
                        vMax: formData.VmaxMitral.value,
                        som: formData.SOMMitral.value,
                        pht: formData.PHTMitral.value,
                        som20: formData.SOM20Mitral.value,
                        dpdt: formData.dpdtMitral.value
                    }
                },

                conclusion: formData.concluzii.value,
                templateVersion: 1,

                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            }

            const docId = `${formData.cnpPacient.value}_${formData.dataConsult.value.replace(/\./g, '-')}`
            const examRef = doc(db, 'exams', docId)

            let existingExam = false;
            if (navigator.onLine) {
                // Check if exam exists for audit log just if online
                try {
                    const { getDoc } = await import('firebase/firestore')
                    const snap = await getDoc(examRef)
                    existingExam = snap.exists()
                } catch (e) {
                    existingExam = false;
                }
            }

            await setDoc(examRef, examData, { merge: true })

            // Log exam action
            await logAction(
                existingExam ? 'update_exam' : 'create_exam',
                'exam',
                docId,
                {},
                { patientId: formData.cnpPacient.value, dateConsult: formData.dataConsult.value }
            )

            if (navigator.onLine) {
                notify.success('Datele au fost salvate cu succes!', 'Salvare')
            } else {
                notify.info('Datele au fost salvate local și se vor sincroniza când revine internetul.', 'Offline')
            }
            return true
        } catch (error) {
            notify.error('Eroare la salvare: ' + (error?.message || 'Unknown error'), 'Salvare')
            return false
        }
    }

    // Reactive query for patient records - returns useCollection composable
    const usePatientRecords = (cnpRef) => {
        const recordsQuery = computed(() => {
            if (!cnpRef.value) return null
            const examsRef = collection(db, 'exams')
            return query(examsRef, where('patientId', '==', cnpRef.value))
        })

        return useCollection(recordsQuery)
    }

    const exportPDF = async () => {
        try {
            // Generate PDF via print
            window.print()

            // Log export to Firestore
            if (formData.cnpPacient.value && formData.dataConsult.value) {
                const examId = `${formData.cnpPacient.value}_${formData.dataConsult.value.replace(/\./g, '-')}`
                const lastName = formData.lastNamePacient?.value || ''
                const firstName = formData.firstNamePacient?.value || ''

                await logPdfExport(examId, {
                    fileName: `buletin_${lastName}_${firstName}_${formData.dataConsult.value}.pdf`,
                    patientName: `${lastName} ${firstName}`.trim(),
                    dateConsult: formData.dataConsult.value
                })

                // Also log in audit trail
                await logAction(
                    'export_pdf',
                    'exam',
                    examId,
                    {},
                    { patientName: `${lastName} ${firstName}`.trim(), dateConsult: formData.dataConsult.value }
                );
            }
        } catch (error) {
            console.error('PDF export error:', error);
            notify.error('Nu s-a putut genera PDF-ul', 'PDF');
        }
    }

    const finalizeaza = async (examId) => {
        try {
            if (!auth.currentUser) {
                notify.error("Trebuie să fii autentificat pentru a finaliza!", "Autentificare");
                return false;
            }
            if (!examId) {
                notify.error("Nu am găsit examId pentru acest buletin.", "Finalizează");
                return false;
            }

            const examRef = doc(db, "exams", examId);

            await setDoc(examRef, {
                status: "final",
                locked: true,
                updatedAt: new Date().toISOString(),
                finalizedAt: new Date().toISOString(),
                finalizedBy: auth.currentUser.uid,
            }, { merge: true });

            await logAction("finalize_exam", "exam", examId, {}, {});
            notify.success("Buletin finalizat și blocat.", "Finalizează");
            return true;
        } catch (e) {
            notify.error("Eroare la finalizare: " + (e?.message || "Unknown"), "Finalizează");
            return false;
        }
    };

    const resetare = () => {
        formData.cnpPacient.value = '';
        formData.lastNamePacient.value = '';
        formData.firstNamePacient.value = '';
        formData.birthDatePacient.value = '';
        formData.sexPacient.value = 'F';
        formData.phonePacient.value = '';
        formData.notesPacient.value = '';
        formData.inaltimePacient.value = '';
        formData.greutatePacient.value = '';
        formData.bsaPacient.value = '';
        formData.DTDVS.value = '';
        formData.DTDVSbsa.value = '';
        formData.DTSVS.value = '';
        formData.DTSVSbsa.value = '';
        formData.SIVd.value = '';
        formData.PPVSd.value = '';
        formData.MasaVS.value = '';
        formData.RTW.value = '';
        formData.FS.value = '';
        formData.FE.value = '';
        formData.FEvol.value = '';
        formData.MAPSE.value = '';
        formData.VTDVS.value = '';
        formData.VTDVSbsa.value = '';
        formData.VTSVS.value = '';
        formData.VTSVSbsa.value = '';
        formData.DPmaxMitral.value = '';
        formData.DPmediuMitral.value = '';
        formData.VmaxMitral.value = '';
        formData.SOMMitral.value = '';
        formData.PHTMitral.value = '';
        formData.SOM20Mitral.value = '';
        formData.dpdtMitral.value = '';
        formData.concluzii.value = '';
        formData.selectedRecord.value = null;
        formData.examStatus.value = 'draft';
        formData.examLocked.value = false;
    }

    const stergeConsultatie = async (examId, { hard = false } = {}) => {
        try {
            if (!auth.currentUser) {
                notify.error("Trebuie să fii autentificat pentru a șterge!", "Autentificare");
                return false;
            }
            if (!examId) {
                notify.error("Nu am găsit examId pentru consultație.", "Ștergere");
                return false;
            }

            const examRef = doc(db, "exams", examId);

            // soft delete
            if (!hard) {
                await setDoc(
                    examRef,
                    {
                        status: "void",
                        locked: false,
                        voidedAt: new Date().toISOString(),
                        voidedBy: auth.currentUser.uid,
                        updatedAt: new Date().toISOString(),
                    },
                    { merge: true }
                );

                await logAction("void_exam", "exam", examId, {}, {});
                notify.success("Consultația a fost anulată.", "Ștergere");
                return true;
            }

            // hard delete
            await deleteDoc(examRef);
            await logAction("delete_exam", "exam", examId, {}, {});
            notify.success("Consultația a fost ștearsă definitiv.", "Ștergere");
            return true;
        } catch (e) {
            notify.error("Eroare la ștergere: " + (e?.message || "Unknown"), "Ștergere");
            return false;
        }
    }

    return {
        salvare,
        exportPDF,
        resetare,
        stergeConsultatie,
        usePatientRecords,
        finalizeaza
    }
}
